const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

export interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  billingCycle: string;
  displayOrder: number;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  translationQuotaDefault: number; // Characters/month default for translations
  googleTranslateApiKey: string | null; // Google Translate API key for this plan
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePlanData {
  name: string;
  slug: string;
  description?: string;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  translationQuotaDefault?: number;
  googleTranslateApiKey?: string;
  price: number;
  billingCycle?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface UpdatePlanData {
  name?: string;
  slug?: string;
  description?: string;
  features?: Record<string, boolean>;
  limits?: Record<string, number>;
  translationQuotaDefault?: number;
  googleTranslateApiKey?: string;
  price?: number;
  billingCycle?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface Feature {
  id: string;
  key: string;
  name: string;
  description: string | null;
  category: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeatureData {
  key: string;
  name: string;
  description?: string;
  category?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface UpdateFeatureData {
  key?: string;
  name?: string;
  description?: string;
  category?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface FeatureCategory {
  value: string;
  label: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

export interface CreateAdminUserData {
  email: string;
  name: string;
  password: string;
  role?: string;
  status?: string;
}

export interface UpdateAdminUserData {
  email?: string;
  name?: string;
  password?: string;
  role?: string;
  status?: string;
}

export interface AdminUserRole {
  value: string;
  label: string;
}

export interface AdminUserStatus {
  value: string;
  label: string;
}

export interface Client {
  id: string;
  name: string;
  logoUrl?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  tenants?: Tenant[];
}

export interface CreateClientData {
  name: string;
  logoUrl?: string;
  status?: string;
  platform: {
    platformName: string;
    slug: string;
    planId: string;
  };
  admin?: {
    name: string;
    email: string;
  };
}

export interface UpdateClientData {
  name?: string;
  logoUrl?: string;
  status?: string;
}

export interface Tenant {
  id: string;
  clientId: string;
  platformName: string;
  slug: string;
  status: string;
  planId: string;
  plan: {
    id: string;
    name: string;
  };
  config: Record<string, unknown> | null;
  mainLogoUrl?: string | null;
  iconUrl?: string | null;
  secondaryLogoUrl?: string | null;
  faviconUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenantDetail extends Tenant {
  plan: {
    id: string;
    name: string;
    features: Record<string, boolean>;
    limits: Record<string, number>;
  };
  schemaProvisioned: boolean;
  bucketProvisioned: boolean;
  bucketName: string | null;
  bucketPublicUrl: string | null;
  dnsProvisioned: boolean;
  dns: {
    appDomain: string;
    adminDomain: string;
    appProvisioned: boolean;
    adminProvisioned: boolean;
  };
}

export interface AdminUserData {
  name: string;
  email: string;
  password?: string;
}

export interface CreateTenantData {
  clientId?: string;
  platformName: string;
  slug: string;
  planId: string;
  config?: {
    primaryColor?: string;
    secondaryColor?: string;
    logo?: string;
    favicon?: string;
  };
  admin?: AdminUserData;
  sendWelcomeEmail?: boolean;
}

export interface CreateTenantResponse extends Tenant {
  adminUser?: {
    id: string;
    email: string;
    name: string;
    role: string;
    passwordGenerated: boolean;
  };
  generatedPassword?: string | null;
}

export interface UpdateTenantData {
  name?: string;
  platformName?: string;
  status?: 'active' | 'inactive' | 'suspended';
  planId?: string;
  config?: {
    primaryColor?: string;
    secondaryColor?: string;
    logo?: string;
    favicon?: string;
  };
}

export interface ApiError {
  message: string;
  statusCode: number;
}

export interface Language {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLanguageData {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  isActive?: boolean;
}

export interface UpdateLanguageData {
  code?: string;
  name?: string;
  nativeName?: string;
  flag?: string;
  isActive?: boolean;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('accessToken');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: 'Erro desconhecido',
        statusCode: response.status,
      }));
      throw error as ApiError;
    }

    return response.json();
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getProfile(): Promise<User> {
    return this.request<User>('/admin/auth/me');
  }

  // Plan endpoints
  async getPlans(includeInactive = false): Promise<Plan[]> {
    const query = includeInactive ? '?includeInactive=true' : '';
    return this.request<Plan[]>(`/plans${query}`);
  }

  async getPlan(id: string): Promise<Plan> {
    return this.request<Plan>(`/plans/${id}`);
  }

  async getPlanBySlug(slug: string): Promise<Plan> {
    return this.request<Plan>(`/plans/slug/${slug}`);
  }

  async getPlanTenantsCount(id: string): Promise<{ count: number }> {
    return this.request<{ count: number }>(`/plans/${id}/tenants-count`);
  }

  async createPlan(data: CreatePlanData): Promise<Plan> {
    return this.request<Plan>('/plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePlan(id: string, data: UpdatePlanData): Promise<Plan> {
    return this.request<Plan>(`/plans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePlan(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/plans/${id}`, {
      method: 'DELETE',
    });
  }

  // Tenant endpoints
  async getTenants(): Promise<Tenant[]> {
    return this.request<Tenant[]>('/tenants');
  }

  async getTenant(slug: string): Promise<TenantDetail> {
    return this.request<TenantDetail>(`/tenants/${slug}`);
  }

  async createTenant(data: CreateTenantData): Promise<CreateTenantResponse> {
    return this.request<CreateTenantResponse>('/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTenant(slug: string, data: UpdateTenantData): Promise<TenantDetail> {
    return this.request<TenantDetail>(`/tenants/${slug}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTenant(slug: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/tenants/${slug}`, {
      method: 'DELETE',
    });
  }

  // Feature endpoints
  async getFeatures(includeInactive = false): Promise<Feature[]> {
    const query = includeInactive ? '?includeInactive=true' : '';
    return this.request<Feature[]>(`/features${query}`);
  }

  async getFeature(id: string): Promise<Feature> {
    return this.request<Feature>(`/features/${id}`);
  }

  async getFeatureByKey(key: string): Promise<Feature> {
    return this.request<Feature>(`/features/key/${key}`);
  }

  async getFeatureCategories(): Promise<FeatureCategory[]> {
    return this.request<FeatureCategory[]>('/features/categories');
  }

  async createFeature(data: CreateFeatureData): Promise<Feature> {
    return this.request<Feature>('/features', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFeature(id: string, data: UpdateFeatureData): Promise<Feature> {
    return this.request<Feature>(`/features/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFeature(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/features/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin User endpoints
  async getAdminUsers(includeInactive = false): Promise<AdminUser[]> {
    const query = includeInactive ? '?includeInactive=true' : '';
    return this.request<AdminUser[]>(`/admin-users${query}`);
  }

  async getAdminUser(id: string): Promise<AdminUser> {
    return this.request<AdminUser>(`/admin-users/${id}`);
  }

  async getAdminUserRoles(): Promise<AdminUserRole[]> {
    return this.request<AdminUserRole[]>('/admin-users/roles');
  }

  async getAdminUserStatuses(): Promise<AdminUserStatus[]> {
    return this.request<AdminUserStatus[]>('/admin-users/statuses');
  }

  async createAdminUser(data: CreateAdminUserData): Promise<AdminUser> {
    return this.request<AdminUser>('/admin-users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminUser(id: string, data: UpdateAdminUserData): Promise<AdminUser> {
    return this.request<AdminUser>(`/admin-users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminUser(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/admin-users/${id}`, {
      method: 'DELETE',
    });
  }

  // Client endpoints
  async getClients(): Promise<Client[]> {
    return this.request<Client[]>('/clients');
  }

  async getClient(id: string): Promise<Client> {
    return this.request<Client>(`/clients/${id}`);
  }

  async createClient(data: CreateClientData): Promise<Client> {
    return this.request<Client>('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(id: string, data: UpdateClientData): Promise<Client> {
    return this.request<Client>(`/clients/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteClient(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/clients/${id}`, {
      method: 'DELETE',
    });
  }

  // Stats endpoints
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('/stats/dashboard');
  }

  // Languages endpoints
  async getLanguages(): Promise<Language[]> {
    return this.request<Language[]>('/languages');
  }

  async getLanguage(id: string): Promise<Language> {
    return this.request<Language>(`/languages/${id}`);
  }

  async createLanguage(data: CreateLanguageData): Promise<Language> {
    return this.request<Language>('/languages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLanguage(id: string, data: UpdateLanguageData): Promise<Language> {
    return this.request<Language>(`/languages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLanguage(id: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/languages/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleLanguageActive(id: string): Promise<Language> {
    return this.request<Language>(`/languages/${id}/toggle-active`, {
      method: 'PUT',
    });
  }

  // Google Translate API Key endpoints
  async getGoogleTranslateApiKey(): Promise<{ apiKey: string | null }> {
    return this.request<{ apiKey: string | null }>('/languages/settings/google-translate-api-key');
  }

  async setGoogleTranslateApiKey(apiKey: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/languages/settings/google-translate-api-key', {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
  }

  // OpenAI API Key endpoints
  async getOpenAIApiKey(): Promise<{ apiKey: string | null }> {
    return this.request<{ apiKey: string | null }>('/languages/settings/openai-api-key');
  }

  async setOpenAIApiKey(apiKey: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/languages/settings/openai-api-key', {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
  }

  // OpenAI Text Model endpoints
  async getOpenAITextModel(): Promise<{ model: string | null }> {
    return this.request<{ model: string | null }>('/languages/settings/openai-text-model');
  }

  async setOpenAITextModel(model: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/languages/settings/openai-text-model', {
      method: 'PUT',
      body: JSON.stringify({ model }),
    });
  }

  // OpenAI Image Model endpoints
  async getOpenAIImageModel(): Promise<{ model: string | null }> {
    return this.request<{ model: string | null }>('/languages/settings/openai-image-model');
  }

  async setOpenAIImageModel(model: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/languages/settings/openai-image-model', {
      method: 'PUT',
      body: JSON.stringify({ model }),
    });
  }
}

// Stats types
export interface DashboardStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
    suspended: number;
  };
  plans: {
    total: number;
    active: number;
  };
  features: {
    total: number;
    active: number;
  };
  admins: {
    total: number;
    active: number;
  };
  tenantsByPlan: Array<{
    planId: string;
    planName: string;
    count: number;
  }>;
  recentTenants: Array<{
    id: string;
    name: string;
    slug: string;
    status: string;
    planName: string;
    createdAt: string;
  }>;
  tenantGrowth: Array<{
    month: string;
    count: number;
  }>;
}

export const api = new ApiClient(API_URL);
